import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Select,
  useToast,
} from '@adop/ui';
import {
  CreditCard,
  ExternalLink,
  Globe,
  Info,
  MapPin,
  Printer,
  ScrollText,
  Users,
  UtensilsCrossed,
} from 'lucide-react';
import { formatCents, formatLocalDateTime } from '@adop/shared';
import { PLANS } from '@adop/shared/plans';
import type { DomainStatus, RestaurantStatus } from '@adop/shared';
import type { AdminSetPlanRequest } from '@adop/shared/admin';
import {
  fetchAdminRestaurant,
  setAdminRestaurantStatus,
  setRestaurantPlan,
} from '../../lib/admin-api';
import { errorMessage } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { storefrontUrlFor } from '../../lib/storefront';
import { formatDate, humanize, isPrinterOnline, relativeTime } from '../../lib/format';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock } from '../../components/LoadingState';
import { OrderStatusBadge, PaymentStatusBadge, ReservationStatusBadge } from '../../components/StatusBadge';
import { AdminRestaurantStatusBadge } from './AdminRestaurantStatusBadge';
import { AdminStatusModal } from './AdminStatusModal';

const DOMAIN_VARIANTS: Record<DomainStatus, 'success' | 'warning' | 'danger' | 'default'> = {
  active: 'success',
  pending_dns: 'warning',
  pending_ssl: 'warning',
  failed: 'danger',
  removed: 'default',
};

function EmptySection({ icon, title }: { icon: ReactNode; title: string }) {
  return <EmptyState icon={icon} title={title} className="py-8" />;
}

export function AdminRestaurantPage() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const state = useAsync(
    () => (id ? fetchAdminRestaurant(id) : Promise.reject(new Error('Missing restaurant id.'))),
    [id],
  );

  const [statusOpen, setStatusOpen] = useState(false);
  const [nextStatus, setNextStatus] = useState<RestaurantStatus>('suspended');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [planDraft, setPlanDraft] = useState('');
  const [planBusy, setPlanBusy] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  const detail = state.data;

  useEffect(() => {
    if (detail?.restaurant.plan) setPlanDraft(detail.restaurant.plan);
  }, [detail?.restaurant.plan]);

  const openStatus = (status: RestaurantStatus) => {
    setNextStatus(status === 'active' ? 'suspended' : 'active');
    setActionError(null);
    setStatusOpen(true);
  };

  const confirmStatus = async (reason: string) => {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    try {
      await setAdminRestaurantStatus(id, {
        status: nextStatus,
        ...(reason.length > 0 ? { reason } : {}),
      });
      toast({
        title: nextStatus === 'suspended' ? 'Restaurant suspended' : 'Restaurant reactivated',
        variant: 'success',
      });
      setStatusOpen(false);
      state.reload();
    } catch (cause) {
      setActionError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const locationName = (locationId: string): string =>
    detail?.locations.find((location) => location.id === locationId)?.name ?? locationId;

  const locationTimezone = (locationId: string): string =>
    detail?.locations.find((location) => location.id === locationId)?.timezone ?? 'UTC';

  const savePlan = async () => {
    if (!id || planDraft.length === 0) return;
    setPlanBusy(true);
    setPlanError(null);
    try {
      await setRestaurantPlan(id, { plan: planDraft as AdminSetPlanRequest['plan'] });
      toast({ title: 'Plan updated', variant: 'success' });
      state.reload();
    } catch (cause) {
      setPlanError(errorMessage(cause));
    } finally {
      setPlanBusy(false);
    }
  };

  const knownPlan = PLANS.some((plan) => plan.id === planDraft);

  return (
    <div className="space-y-5">
      <PageHeader
        title={detail?.restaurant.name ?? 'Restaurant'}
        description={
          detail ? (
            <span className="flex flex-wrap items-center gap-2">
              <AdminRestaurantStatusBadge status={detail.restaurant.status} />
              <Badge variant="outline">{detail.restaurant.plan}</Badge>
              <span className="text-zinc-500">
                {detail.restaurant.stripe_charges_enabled ? 'Stripe enabled' : 'Stripe not enabled'} ·
                created {formatDate(detail.restaurant.created_at)}
              </span>
            </span>
          ) : (
            'Loading…'
          )
        }
        action={
          detail ? (
            <div className="flex items-center gap-2">
              <Link
                to="/admin/restaurants"
                className="text-sm font-medium text-zinc-600 underline hover:text-zinc-900"
              >
                All restaurants
              </Link>
              <Button
                variant={detail.restaurant.status === 'active' ? 'danger' : 'primary'}
                onClick={() => openStatus(detail.restaurant.status)}
              >
                {detail.restaurant.status === 'active' ? 'Suspend' : 'Reactivate'}
              </Button>
            </div>
          ) : null
        }
      />

      <Alert variant="info">
        <span className="inline-flex items-center gap-1.5">
          <Info className="h-4 w-4 shrink-0" /> Opening this support view is recorded in the audit log as
          <code className="rounded bg-sky-100 px-1">admin.support_view</code>.
        </span>
      </Alert>

      {state.error ? <Alert>{state.error}</Alert> : null}
      {state.loading ? <LoadingBlock /> : null}

      {detail ? (
        <div className="space-y-5">
          <Card>
            <CardHeader className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-zinc-500" />
              <CardTitle>Subscription plan</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {planError ? <Alert>{planError}</Alert> : null}
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-56">
                  <Select
                    label="Plan"
                    value={planDraft}
                    onChange={(event) => setPlanDraft(event.target.value)}
                  >
                    {PLANS.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.label}
                      </option>
                    ))}
                    {!knownPlan && planDraft.length > 0 ? (
                      <option value={planDraft}>{planDraft} (unknown plan)</option>
                    ) : null}
                  </Select>
                </div>
                <Button
                  loading={planBusy}
                  disabled={planDraft === detail.restaurant.plan}
                  onClick={() => void savePlan()}
                >
                  Save plan
                </Button>
              </div>
              <p className="text-xs text-zinc-500">
                Changing the plan is written to the audit log. It is a usage allowance only — nothing is
                charged here.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-zinc-500" />
              <CardTitle>Locations</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {detail.locations.length === 0 ? (
                <EmptySection icon={<MapPin className="h-6 w-6" />} title="No locations" />
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {detail.locations.map((location) => (
                    <li
                      key={location.id}
                      className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm"
                    >
                      <span className="font-medium text-zinc-900">{location.name}</span>
                      {location.published ? (
                        <Badge variant="success">Published</Badge>
                      ) : (
                        <Badge variant="warning">Unpublished</Badge>
                      )}
                      <span className="text-zinc-500">/{location.slug}</span>
                      <a
                        href={storefrontUrlFor(location.slug)}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-zinc-900 underline"
                      >
                        {location.slug}.adop.app <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader className="flex items-center gap-2">
                <Users className="h-4 w-4 text-zinc-500" />
                <CardTitle>Members</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {detail.members.length === 0 ? (
                  <EmptySection icon={<Users className="h-6 w-6" />} title="No members" />
                ) : (
                  <ul className="divide-y divide-zinc-100">
                    {detail.members.map((member) => (
                      <li key={member.user_id} className="px-5 py-3 text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-zinc-900">
                            {member.full_name ?? member.email ?? member.user_id}
                          </span>
                          <Badge variant="outline">{humanize(member.role)}</Badge>
                        </div>
                        {member.email ? <p className="text-xs text-zinc-500">{member.email}</p> : null}
                        <p className="mt-1 text-xs text-zinc-500">
                          {member.location_ids && member.location_ids.length > 0
                            ? `Locations: ${member.location_ids.map(locationName).join(', ')}`
                            : 'All locations'}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex items-center gap-2">
                <Printer className="h-4 w-4 text-zinc-500" />
                <CardTitle>Printers</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {detail.devices.length === 0 ? (
                  <EmptySection icon={<Printer className="h-6 w-6" />} title="No printers" />
                ) : (
                  <ul className="divide-y divide-zinc-100">
                    {detail.devices.map((device) => {
                      const online = !device.revoked_at && isPrinterOnline(device.last_seen_at);
                      return (
                        <li key={device.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                          <span className="font-medium text-zinc-900">{device.name}</span>
                          <Badge variant={online ? 'success' : 'default'}>
                            {device.revoked_at ? 'Revoked' : online ? 'Online' : 'Offline'}
                          </Badge>
                          <span className="ml-auto text-xs text-zinc-500">
                            {device.last_seen_at ? `Last seen ${relativeTime(device.last_seen_at)}` : 'Never seen'}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-zinc-500" />
              <CardTitle>Domains</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {detail.domains.length === 0 ? (
                <EmptySection icon={<Globe className="h-6 w-6" />} title="No custom domains" />
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {detail.domains.map((domain) => (
                    <li key={domain.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                      <span className="font-medium text-zinc-900">{domain.hostname}</span>
                      <span className="text-zinc-500">{locationName(domain.location_id)}</span>
                      <Badge variant={DOMAIN_VARIANTS[domain.status]} className="ml-auto">
                        {humanize(domain.status)}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex items-center gap-2">
              <UtensilsCrossed className="h-4 w-4 text-zinc-500" />
              <CardTitle>Recent orders</CardTitle>
            </CardHeader>
            <div className="overflow-x-auto">
              {detail.recent_orders.length === 0 ? (
                <EmptySection icon={<UtensilsCrossed className="h-6 w-6" />} title="No recent orders" />
              ) : (
                <table className="w-full min-w-[36rem] text-left text-sm">
                  <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-5 py-2 font-medium">Order</th>
                      <th className="px-5 py-2 font-medium">Location</th>
                      <th className="px-5 py-2 font-medium">Status</th>
                      <th className="px-5 py-2 font-medium">Payment</th>
                      <th className="px-5 py-2 text-right font-medium">Total</th>
                      <th className="px-5 py-2 text-right font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {detail.recent_orders.map((order) => (
                      <tr key={order.id}>
                        <td className="px-5 py-3 font-medium text-zinc-900">#{order.order_number}</td>
                        <td className="px-5 py-3 text-zinc-600">{order.location_name}</td>
                        <td className="px-5 py-3">
                          <OrderStatusBadge status={order.status} />
                        </td>
                        <td className="px-5 py-3">
                          <PaymentStatusBadge status={order.payment_status} />
                        </td>
                        <td className="px-5 py-3 text-right font-medium text-zinc-900">
                          {formatCents(order.total_cents)}
                        </td>
                        <td className="px-5 py-3 text-right text-xs text-zinc-500">
                          {relativeTime(order.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader className="flex items-center gap-2">
              <ScrollText className="h-4 w-4 text-zinc-500" />
              <CardTitle>Recent reservations</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {detail.recent_reservations.length === 0 ? (
                <EmptySection icon={<ScrollText className="h-6 w-6" />} title="No recent reservations" />
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {detail.recent_reservations.map((reservation) => (
                    <li
                      key={reservation.id}
                      className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm"
                    >
                      <span className="font-medium text-zinc-900">
                        Party of {reservation.party_size}
                      </span>
                      <ReservationStatusBadge status={reservation.status} />
                      <span className="text-zinc-500">{locationName(reservation.location_id)}</span>
                      <span className="ml-auto text-xs text-zinc-500">
                        {formatLocalDateTime(
                          reservation.requested_for,
                          locationTimezone(reservation.location_id),
                        )}{' '}
                        · requested {relativeTime(reservation.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}

      <AdminStatusModal
        open={statusOpen}
        restaurantName={detail?.restaurant.name ?? ''}
        nextStatus={nextStatus}
        busy={busy}
        error={actionError}
        onClose={() => {
          if (!busy) setStatusOpen(false);
        }}
        onConfirm={(reason) => void confirmStatus(reason)}
      />
    </div>
  );
}
